import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import Select from 'react-select';
import { Error } from '../../../design/elements';
import Note from '../../../note';
import {
  TableWrapper,
  Table,
  Th,
  SubHeading,
  SubHeadingWithNote,
  SubHeadingTitle,
  Td,
  TermRow,
  PriceContainer,
  Checkbox,
  PriceText,
  DashText,
  DropdownContainer,
  DataRow,
  CounterContainer,
  CounterButton,
  CounterValue
} from './css';
import { PLAN_NAMES, TERM_CONFIG, THEME_PACKS_CONFIG, ADD_ONS_CONFIG, PROMOTIONS_CONFIG, SET_TOP_BOX_CONFIG, INSTALLATION_CONFIG, MAX_TOTAL_STBS, isAddOnAvailableForProfile } from './config';
import { businessOptikTvFieldNames, selfInstall, waivedMultiProductOrder } from '../../../utils/constant';
import { addProductVulcan } from '../../vulcanPricing/customerInformation/customerInformationFieldNames';
import { getLocale } from '../../../misc';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import getBusinessOptikTvPricing from '../../vulcanPricing/businessOptikTv/businessOptikTv';

const BusinessOptikTvTable = ({ 
  fieldName, 
  formFields, 
  onChange,
  error,
  initialData = null
  }) => {
  // State management for counters, checkboxes, and dropdowns
  const [counters, setCounters] = useState(get(initialData, 'counters', {}));
  const [checkboxes, setCheckboxes] = useState(get(initialData, 'checkboxes', {}));
  const [dropdownSelections, setDropdownSelections] = useState(get(initialData, 'dropdownSelections', {}));

  // Get the selected monthly rate plan and term to determine what to show
  const { monthlyRatePlanOptikTvVul, termBusinessOptikTvVul, profileTypeOptikTvVul } = businessOptikTvFieldNames;
  const selectedRatePlan = get(formFields, `${monthlyRatePlanOptikTvVul}.value`);
  const selectedTerm = get(formFields, `${termBusinessOptikTvVul}.value`);
  const selectedProfileType = get(formFields, `${profileTypeOptikTvVul}.value`);

  // Get translations for profile types
  const locale = getLocale();
  const translations = locale === 'en-US' ? EN : FR;
  const {
    privateHomeBasedBusinessOpus,
    publicOpus,
    privateLegacy,
    publicLegacy,
    selectInstallationType,
    publicLiquor
  } = translations.vulcanWebform.businessOptikTv;

  // Get the plan index for backward compatibility
  const selectedPlanIndex = PLAN_NAMES.findIndex(plan => plan === selectedRatePlan);

  // Track if this is the initial load to prevent resetting on mount
  const isInitialLoad = useRef(true);
  const previousRatePlan = useRef(selectedRatePlan);
  const previousTerm = useRef(selectedTerm);
  const previousProfileType = useRef(selectedProfileType);
  const installationPreselected = useRef(false);

  // Helper function to get data with backward compatibility
  const getDataWithFallback = (dataObject, keyPrefix) => {
    if (!dataObject || !selectedRatePlan) return undefined;
    
    // Try new format first (plan name)
    const newKey = `${keyPrefix}_${selectedRatePlan}`;
    if (dataObject[newKey] !== undefined) {
      return dataObject[newKey];
    }
    
    // Fallback to old format (plan index) for backward compatibility
    if (selectedPlanIndex !== -1) {
      const oldKey = `${keyPrefix}_${selectedPlanIndex}`;
      return dataObject[oldKey];
    }
    
    return undefined;
  };

const isAnyPromotionSelected = (plan, currentRowType, objectData) => {
  return Object.entries(objectData).some(([rowType]) => {
    if (rowType !== currentRowType) {
      const isSelected = checkboxes[`${rowType}_${plan}`];
      return isSelected;
    }
    return false;
  });
};
    
  // Reset table selections when rate plan, term, or profile type changes (but not on initial load)
  useEffect(() => {
    // Skip reset on initial load
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      previousRatePlan.current = selectedRatePlan;
      previousTerm.current = selectedTerm;
      previousProfileType.current = selectedProfileType;
      return;
    }

    // Only reset if the selections actually changed
    if (previousRatePlan.current !== selectedRatePlan || 
        previousTerm.current !== selectedTerm || 
        previousProfileType.current !== selectedProfileType) {
      setCounters({});
      setCheckboxes({});
      setDropdownSelections({});
      installationPreselected.current = false; // Reset installation preselection flag
      previousRatePlan.current = selectedRatePlan;
      previousTerm.current = selectedTerm;
      previousProfileType.current = selectedProfileType;
    }
  }, [selectedRatePlan, selectedTerm, selectedProfileType]);

  // Validation function for theme pack requirements (memoized)
  // Helper function to get theme pack count for display
  const getThemePackCount = useCallback(() => {
    const themePackRequirements = {
      'Choices': 3,
      'More Choices': 4,
      'Optik TV': 3,
      'Optik TV Elite': 4
    };

    const requiredCount = themePackRequirements[selectedRatePlan];
    if (!requiredCount) {
      return { totalSelected: 0, requiredCount: 0 };
    }

    // Count total selected theme packs across all sections
    let totalSelected = 0;
    const themePackSections = [
      'Included Theme Packs',
      // 'Additional Theme Packs (Additional Fee)',
      // 'A La Carte Channels (Additional Fee)',
      // 'Premium Theme Packs'
    ];

    themePackSections.forEach(section => {
      const selections = getDataWithFallback(dropdownSelections, section);
      if (selections && Array.isArray(selections)) {
        totalSelected += selections.length;
      }
    });

    return { totalSelected, requiredCount };
  }, [selectedRatePlan, dropdownSelections]);

  const validateThemePacks = useCallback(() => {
    const { totalSelected, requiredCount } = getThemePackCount();
    
    if (requiredCount === 0) {
      return { isValid: true, error: null };
    }

    const isValid = totalSelected === requiredCount;
    const errorMessage = isValid ? null : `${selectedRatePlan} plan requires exactly ${requiredCount} theme packs to be selected. Currently selected: ${totalSelected}`;

    return { isValid, error: errorMessage, totalSelected, requiredCount };
  }, [selectedRatePlan, getThemePackCount]);

  // Validation function for Set Top Box limits
  const validateSetTopBoxes = useCallback(() => {
    const wiredCount = getDataWithFallback(counters, 'Wired') || 1;
    const wirelessCount = getDataWithFallback(counters, 'Wireless') || 0;
    const totalStbs = wiredCount + wirelessCount;

    if (totalStbs > MAX_TOTAL_STBS) {
      return {
        isValid: false,
        error: `If more than ${MAX_TOTAL_STBS} set top boxes are required, additional internet connections will be needed. Include these details in your order comments.`,
        totalStbs,
        maxAllowed: MAX_TOTAL_STBS
      };
    }

    return { isValid: true, error: null, totalStbs, maxAllowed: MAX_TOTAL_STBS };
  }, [counters]);

  // Function to determine which installation options should be visible based on profile type
  const getVisibleInstallationOptions = useCallback(() => {
    if (!selectedProfileType) return [];

    const visibleOptions = [];
    
    // "Self Install (DIY)" should appear for Private/Home Based Business (OPUS), Public (OPUS)
    if (selectedProfileType === privateHomeBasedBusinessOpus || selectedProfileType === publicOpus || selectedProfileType === publicLiquor) {
      visibleOptions.push('Self Install (DIY)');
    }
    
    // "Professional Install" should appear for All Profile Types
    visibleOptions.push('Professional Install');
    
    // "Waived Professional Install (Multi Product Order)" should appear for Private/Home Based Business (OPUS), Public (OPUS)
    if (selectedProfileType === privateHomeBasedBusinessOpus || selectedProfileType === publicOpus || selectedProfileType === publicLiquor) {
      visibleOptions.push('Waived Professional Install (Multi Product Order)');
    }
    
    // "Waived Professional Install (If moving from a competitor)" should appear for Public (Legacy) & Private (Legacy)
    if (selectedProfileType === publicLegacy || selectedProfileType === privateLegacy) {
      visibleOptions.push('Waived Professional Install (If moving from a competitor)');
    }
    
    return visibleOptions;
  }, [selectedProfileType, privateHomeBasedBusinessOpus, publicOpus, publicLegacy, privateLegacy, publicLiquor]);

  // Validation: at least one installation type must be selected for TV product
  const validateInstallationType = useCallback(() => {
    const visibleOptions = getVisibleInstallationOptions();
    if (visibleOptions.length === 0) return { isValid: true, error: null };
    const hasSelection = visibleOptions.some(
      rowType => checkboxes[`${rowType}_${selectedRatePlan}`] === true
    );
    const errorMessage =
      selectInstallationType || 'Please select an installation type.';
    return {
      isValid: hasSelection,
      error: hasSelection ? null : errorMessage
    };
  }, [
    checkboxes,
    selectedRatePlan,
    getVisibleInstallationOptions,
    selectInstallationType
  ]);

  // Use ref to store the latest onChange to avoid infinite loops
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // Preselect installation options when component mounts or product selection changes
  useEffect(() => {
    if (selectedRatePlan && selectedProfileType && !installationPreselected.current) {
      const productSelection = get(formFields, `${addProductVulcan}.value`, []);
      const visibleOptions = getVisibleInstallationOptions();
      
      // Only run if Business Optik TV is selected
      if (productSelection.includes('Business Optik TV')) {
        if (productSelection.length === 1 && visibleOptions.includes('Self Install (DIY)')) {
          // Preselect Self Install (DIY) if only Business Optik TV is selected and it's visible
          const selfInstallKey = `${selfInstall}_${selectedRatePlan}`;
          setCheckboxes(prev => ({
            ...prev,
            [selfInstallKey]: true
          }));
          installationPreselected.current = true;
        } else if (productSelection.length > 1 && visibleOptions.includes('Waived Professional Install (Multi Product Order)')) {
          // Preselect Waived Professional Install (Multi Product Order) if Business Optik TV + other products and it's visible
          const waivedMultiKey = `${waivedMultiProductOrder}_${selectedRatePlan}`;
          setCheckboxes(prev => ({
            ...prev,
            [waivedMultiKey]: true
          }));
          installationPreselected.current = true;
        }
      }
    }
  }, [selectedRatePlan, selectedProfileType, formFields, getVisibleInstallationOptions]);

  // Sanitize legacy data: Installation is now a radio group, so for each plan keep at most
  // one installation key true. Runs only when rate plan / profile changes (and on initial mount).
  useEffect(() => {
    if (!selectedRatePlan) return;
    setCheckboxes(prev => {
      const installRows = Object.keys(INSTALLATION_CONFIG);
      const trueRows = installRows.filter(
        rowType => prev[`${rowType}_${selectedRatePlan}`]
      );
      if (trueRows.length <= 1) return prev;
      const next = { ...prev };
      trueRows.slice(1).forEach(rowType => {
        next[`${rowType}_${selectedRatePlan}`] = false;
      });
      return next;
    });
  }, [selectedRatePlan, selectedProfileType]);

  // Initialize included addons in checkboxes state when rate plan is selected
  // This ensures included items are sent in the payload even though they're disabled
  // Only set included add-ons that are available for the current profile type (see config allowedProfileTypes)
  useEffect(() => {
    if (!selectedRatePlan || selectedPlanIndex === -1) return;

    const profileTypeTranslations = get(translations, 'vulcanWebform.businessOptikTv', {});

    setCheckboxes(prev => {
      const updated = { ...prev };
      let hasChanges = false;

      // Check addons - if price is "Included", ensure it's in state with value true (only if allowed for profile)
      Object.entries(ADD_ONS_CONFIG).forEach(([rowType, config]) => {
        if (!isAddOnAvailableForProfile(rowType, selectedProfileType, profileTypeTranslations)) return;

        const price = config.prices[selectedPlanIndex];
        if (price === 'Included') {
          const checkboxId = `${rowType}_${selectedRatePlan}`;
          // Set to true if not already set to true (this ensures included items are in the payload)
          if (updated[checkboxId] !== true) {
            updated[checkboxId] = true;
            hasChanges = true;
          }
        }
      });

      return hasChanges ? updated : prev;
    });
  }, [selectedRatePlan, selectedPlanIndex, selectedProfileType, translations]);

  // Expose validation through onChange callback (optimized)
  useEffect(() => {
    if (onChangeRef.current && selectedRatePlan) {
      const themePackValidation = validateThemePacks();
      const stbValidation = validateSetTopBoxes();
      const installationValidation = validateInstallationType();

      // Combine validations - all must be valid for overall validity (including installation type)
      const overallValidation = {
        isValid:
          themePackValidation.isValid &&
          stbValidation.isValid &&
          installationValidation.isValid,
        error:
          themePackValidation.error ||
          stbValidation.error ||
          installationValidation.error,
        themePacks: themePackValidation,
        setTopBoxes: stbValidation,
        installationType: installationValidation
      };

      const dataToSend = {
        counters,
        checkboxes,
        dropdownSelections,
        validation: overallValidation
      };

      onChangeRef.current({
        target: {
          name: fieldName,
          value: dataToSend,
          type: 'OptikTvTable'
        }
      });
    }
  }, [
    counters,
    checkboxes,
    dropdownSelections,
    selectedRatePlan,
    fieldName,
    validateThemePacks,
    validateSetTopBoxes,
    validateInstallationType
  ]);

  // Hide table until both plan and term are selected
  if (!selectedRatePlan || !selectedTerm) {
    return null;
  }
  
  // Determine which plan columns to show (selectedRatePlan is guaranteed to exist here)
  const getVisiblePlans = () => {
    // Use the plan index from the top level
    if (selectedPlanIndex === -1) {
      // If selected plan not found, return empty (this shouldn't happen)
      return [];
    }
    
    // Return only the selected plan column
    return [{ plan: selectedRatePlan, index: selectedPlanIndex }];
  };

  const visiblePlans = getVisiblePlans();

  // Safety check - if no visible plans, don't render the table
  if (visiblePlans.length === 0) {
    return null;
  }

  const renderCounterCell = (rowType, planName) => {
    const counterId = `${rowType}_${planName}`;
    const config = SET_TOP_BOX_CONFIG[rowType];
    const currentValue = getDataWithFallback(counters, rowType) !== undefined ? getDataWithFallback(counters, rowType) : config.defaultValue;
    
    const handleIncrement = () => {
      // Check total STB limit before incrementing
      const wiredCount = getDataWithFallback(counters, 'Wired') || 1;
      const wirelessCount = getDataWithFallback(counters, 'Wireless') || 0;
      const currentTotal = wiredCount + wirelessCount;
      
      if (currentTotal >= MAX_TOTAL_STBS) {
        // Show warning popup
        // eslint-disable-next-line no-alert
        alert(`If more than ${MAX_TOTAL_STBS} set top boxes are required, additional internet connections will be needed. Include these details in your order comments.`);
        return;
      }
      
      if (currentValue < config.max) {
        setCounters(prev => ({
          ...prev,
          [counterId]: currentValue + 1
        }));
      }
    };

    const handleDecrement = () => {
      if (currentValue > config.min) {
        setCounters(prev => ({
          ...prev,
          [counterId]: currentValue - 1
        }));
      }
    };

    return (
      <Td key={planName}>
        <CounterContainer>
          <CounterButton
            type="button"
            onClick={handleDecrement}
            disabled={currentValue <= config.min}
          >
            -
          </CounterButton>
          <CounterValue>{currentValue}</CounterValue>
          <CounterButton
            type="button"
            onClick={handleIncrement}
            disabled={currentValue >= config.max}
          >
            +
          </CounterButton>
        </CounterContainer>
      </Td>
    );
  };

  const renderPriceCell = (price, termType, planName) => {
    if (price === '-') {
      return (
        <Td key={planName}>
          <DashText>-</DashText>
        </Td>
      );
    }
    
    const checkboxId = `${termType}_${planName}`;
    // Term checkbox should be checked and disabled by default
    const isChecked = getDataWithFallback(checkboxes, termType, true) || true;

    const handleCheckboxChange = (event) => {
      const { checked } = event.target;
      setCheckboxes(prev => ({
        ...prev,
        [checkboxId]: checked
      }));
      
      // Optional: Call onChange prop if provided to sync with parent form
      if (onChange) {
        onChange({
          target: {
            name: checkboxId,
            value: checked,
            type: 'checkbox'
          }
        });
      }
    };
    
    return (
      <Td key={planName}>
        <PriceContainer>
          <Checkbox 
            type="checkbox" 
            name={checkboxId}
            checked={isChecked}
            onChange={handleCheckboxChange}
            disabled
          />
          <PriceText>{price}</PriceText>
        </PriceContainer>
      </Td>
    );
  };

  const renderDropdownCell = (options, showPrice, rowType, planName) => {
    
    // Special handling for specific rows and plans
    if (rowType === 'Included Theme Packs') {
      // Show "-" for Basics (BTL), Core, Optik TV Basic
      if (planName === 'Basics (BTL)' || planName === 'Core' || planName === 'Optik TV Basic') {
        return (
          <Td key={planName}>
            <DashText>-</DashText>
          </Td>
        );
      }
    }
    
    if (rowType === 'Premium Theme Packs') {
      // Show "-" for Public (Legacy) and Private (Legacy) profile types
      if (selectedProfileType === publicLegacy || selectedProfileType === privateLegacy) {
        return (
          <Td key={planName}>
            <DashText>-</DashText>
          </Td>
        );
      }
      // Show dropdown only for Basics (BTL), Core, Choices, More Choices
      if (planName === 'Basics (BTL)' || planName === 'Core' || planName === 'Choices' || planName === 'More Choices') {
        // Special pricing for More Choices plan
        if (planName === 'More Choices') {
          // Override options for More Choices - Crave is $0 and included
          const moreChoicesOptions = [{ name: 'Crave', price: '$0 - Included' }];
          const selectOptions = moreChoicesOptions.map(option => ({
            value: option.name,
            label: showPrice ? `${option.name} (${option.price})` : option.name,
            price: option.price
          }));
          
          const customStyles = {
            option: (provided) => ({
              ...provided,
              borderBottom: '1px solid #e3e3e3',
              fontWeight: 100,
              color: '#555',
              backgroundColor: 'white',
              fontSize: '1em'
            }),
            control: (provided) => ({
              ...provided,
              borderRadius: '3px',
              border: '1px solid #ccc',
              fontSize: '1em',
              minHeight: '2rem'
            }),
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: '#4b286d',
              color: 'white'
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: 'white',
              fontSize: '1em'
            }),
            multiValueRemove: (provided) => ({
              ...provided,
              color: 'white',
              ':hover': {
                backgroundColor: '#3a1f52',
                color: 'white'
              }
            }),
            menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
          };
          
          return (
            <Td key={planName}>
              <DropdownContainer>
                <Select
                  isMulti
                  isDisabled
                  name={`${rowType}_${planName}`}
                  options={selectOptions}
                  styles={customStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  value={selectOptions} // Preselected with Crave
                  placeholder="Select..."
                  // onChange handler will be implemented later
                />
              </DropdownContainer>
            </Td>
          );
        }
        // Show normal Crave dropdown for other plans (Basics BTL, Core, Choices)
      } else {
        // Show "-" for Optik TV Basic, Optik TV, Optik TV Elite
        return (
          <Td key={planName}>
            <DashText>-</DashText>
          </Td>
        );
      }
    }

    // Convert options to react-select format
    const selectOptions = options.map(option => ({
      value: option.name,
      label: showPrice ? `${option.name} (${option.price})` : option.name,
      price: option.price
    }));

    // Custom styles matching the existing pattern
    const customStyles = {
      option: (provided) => ({
        ...provided,
        borderBottom: '1px solid #e3e3e3',
        fontWeight: 100,
        color: '#555',
        backgroundColor: 'white',
        fontSize: '1em'
      }),
      control: (provided) => ({
        ...provided,
        borderRadius: '3px',
        border: '1px solid #ccc',
        fontSize: '1em',
        minHeight: '2rem'
      }),
      multiValue: (provided) => ({
        ...provided,
        backgroundColor: '#4b286d',
        color: 'white'
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        color: 'white',
        fontSize: '1em'
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        color: 'white',
        ':hover': {
          backgroundColor: '#3a1f52',
          color: 'white'
        }
      }),
      menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
    };

    const dropdownId = `${rowType}_${planName}`;
    const currentValue = getDataWithFallback(dropdownSelections, rowType) || [];

    const handleDropdownChange = (selectedOptions) => {
      const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
      setDropdownSelections(prev => ({
        ...prev,
        [dropdownId]: values
      }));
    };

    return (
      <Td key={planName}>
        <DropdownContainer>
          <Select
            isMulti
            name={dropdownId}
            options={selectOptions}
            styles={customStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            placeholder="Select..."
            value={selectOptions.filter(option => currentValue.includes(option.value))}
            onChange={handleDropdownChange}
          />
        </DropdownContainer>
      </Td>
    );
  };

  const renderCheckboxCell = (price, rowType, planName, disableField, treatAsIncluded) => {
    if (price === '-') {
      return (
        <Td key={planName}>
          <DashText>-</DashText>
        </Td>
      );
    }

    // When price is "Included" but profile doesn't get included (e.g. not privateHomeBasedBusinessOpus), show simple $0.00
    const includedForProfile = treatAsIncluded !== undefined ? treatAsIncluded : (price === 'Included');
    const isIncluded = price === 'Included' && includedForProfile;
    const displayPrice = price === 'Included' ? '$0.00' : price;
    const label = isIncluded ? `${displayPrice} - Included` : displayPrice;
    const checkboxId = `${rowType}_${planName}`;
    const isChecked = getDataWithFallback(checkboxes, rowType) || isIncluded;

    const handleCheckboxChange = (event) => {
      const { checked } = event.target;
      setCheckboxes(prev => ({
        ...prev,
        [checkboxId]: checked
      }));
      
      // Optional: Call onChange prop if provided to sync with parent form
      if (onChange) {
        onChange({
          target: {
            name: checkboxId,
            value: checked,
            type: 'checkbox'
          }
        });
      }
    };

    return (
      <Td key={planName}>
        <PriceContainer>
          <Checkbox 
            type="checkbox" 
            name={checkboxId}
            checked={isChecked}
            disabled={isIncluded || disableField}
            onChange={handleCheckboxChange}
          />
          <PriceText>{label}</PriceText>
        </PriceContainer>
      </Td>
    );
  };

  // Radio variant for mutually-exclusive rows (e.g. Installation).
  // Stores selection in the same `checkboxes` map (keys `${rowType}_${planName}`) so
  // pricing, order summary, validation and payload shape are unchanged. Selecting one
  // option clears the other options in the same `group` for the given plan.
  const renderRadioCell = (price, rowType, planName, group) => {
    if (price === '-') {
      return (
        <Td key={planName}>
          <DashText>-</DashText>
        </Td>
      );
    }

    const displayPrice = price === 'Included' ? '$0.00' : price;
    const checkboxId = `${rowType}_${planName}`;
    const groupName = `${group}_${planName}`;
    const isChecked = !!checkboxes[checkboxId];

    const handleRadioChange = () => {
      setCheckboxes(prev => {
        const next = { ...prev };
        // Clear every other option in the same group for this plan
        Object.keys(INSTALLATION_CONFIG).forEach(otherRow => {
          if (otherRow !== rowType) {
            next[`${otherRow}_${planName}`] = false;
          }
        });
        next[checkboxId] = true;
        return next;
      });

      // Mirror the existing checkbox onChange contract for parent sync
      if (onChange) {
        onChange({
          target: {
            name: checkboxId,
            value: true,
            type: 'checkbox'
          }
        });
      }
    };

    return (
      <Td key={planName}>
        <PriceContainer>
          <Checkbox
            as="input"
            type="radio"
            name={groupName}
            checked={isChecked}
            onChange={handleRadioChange}
          />
          <PriceText style={{ minWidth: '3.5rem', textAlign: 'left' }}>{displayPrice}</PriceText>
        </PriceContainer>
      </Td>
    );
  };
  
  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            <Th leftAlign>Business Optik TV Plan</Th>
            {visiblePlans.map(({ plan, index }) => (
              <Th key={index}>{plan}</Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Term Section */}
          <tr>
            <SubHeading colSpan={visiblePlans.length + 1}>Term</SubHeading>
          </tr>
          {Object.entries(TERM_CONFIG)
            .filter(([termType]) => termType === selectedTerm)
            .map(([termType, termData]) => (
              <TermRow key={termType}>
                <Td leftAlign>{termType}</Td>
                {visiblePlans.map(({ plan, index }) => 
                  renderPriceCell(termData.prices[index], termType, plan)
                )}
              </TermRow>
            ))}
          
          {/* Theme Packs Section */}
          <tr>
            <SubHeading colSpan={visiblePlans.length + 1}>Theme Packs</SubHeading>
          </tr>
          {Object.entries(THEME_PACKS_CONFIG).map(([rowType, config]) => {
            // Get theme pack count for "Included Theme Packs" display
            const { totalSelected, requiredCount } = getThemePackCount();
            const displayText = rowType === 'Included Theme Packs' && requiredCount > 0 
              ? `${rowType} (${totalSelected}/${requiredCount})`
              : rowType;
            
            return (
              <DataRow key={rowType}>
                <Td leftAlign>{displayText}</Td>
                {visiblePlans.map(({ plan }) => 
                  renderDropdownCell(config.options, config.showPrice, rowType, plan)
                )}
              </DataRow>
            );
          })}
          
          {/* Add-Ons Section */}
          <tr>
            <SubHeading colSpan={visiblePlans.length + 1}>Add-Ons</SubHeading>
          </tr>
          {Object.entries(ADD_ONS_CONFIG)
            .filter(([rowType]) => isAddOnAvailableForProfile(rowType, selectedProfileType, get(translations, 'vulcanWebform.businessOptikTv', {})))
            .map(([rowType, config]) => (
              <DataRow key={rowType}>
                <Td leftAlign>{rowType}</Td>
                {visiblePlans.map(({ plan, index }) => {
                  const price = config.prices[index];
                  const treatAsIncluded = price === 'Included' && isAddOnAvailableForProfile(rowType, selectedProfileType, get(translations, 'vulcanWebform.businessOptikTv', {}));
                  return renderCheckboxCell(price, rowType, plan, undefined, treatAsIncluded);
                })}
              </DataRow>
            ))}
          
          {/* Promotions Section */}
          <tr>
            <SubHeading colSpan={visiblePlans.length + 1}>Promotions</SubHeading>
          </tr>
          {Object.entries(PROMOTIONS_CONFIG).map(([rowType, config]) => (
            <DataRow key={rowType}>
              <Td leftAlign>{rowType}</Td>
              {visiblePlans.map(({ plan, index }) => {
                const disableField = isAnyPromotionSelected(
                  plan,
                  rowType,
                  PROMOTIONS_CONFIG
                );
                return renderCheckboxCell(config.prices[index], rowType, plan, disableField);
              })}
            </DataRow>
          ))}
          
          {/* Set Top Box Section */}
          <tr>
            <SubHeadingWithNote colSpan={visiblePlans.length + 1}>
              <SubHeadingTitle>Set Top Box</SubHeadingTitle>
              <Note
                type="text"
                text="Note: Customer must have at least 1 wired STB. 1 Wired STB is included with Optik TV and TV Elite Rate Plans"
                isShowNoteBackground
              />
            </SubHeadingWithNote>
          </tr>
          {Object.entries(SET_TOP_BOX_CONFIG).map(([rowType]) => (
            <DataRow key={rowType}>
              <Td leftAlign>{rowType}</Td>
              {visiblePlans.map(({ plan }) => 
                renderCounterCell(rowType, plan)
              )}
            </DataRow>
          ))}
          
          {/* Installation Section */}
          <tr>
            <SubHeadingWithNote colSpan={visiblePlans.length + 1}>
              <SubHeadingTitle>Installation</SubHeadingTitle>
              <Note
                type="text"
                text="Note: Self Install (DIY) is not available for Legacy Optik customers, customers in Tier 3 communities, or customers ordering multiple products that will require fielded install."
                isShowNoteBackground
              />
            </SubHeadingWithNote>
          </tr>
          {Object.entries(INSTALLATION_CONFIG)
            .filter(([rowType]) => getVisibleInstallationOptions().includes(rowType))
            .map(([rowType, config]) => (
              <DataRow key={rowType}>
                <Td leftAlign>{rowType}</Td>
                {visiblePlans.map(({ plan, index }) =>
                  renderRadioCell(config.prices[index], rowType, plan, 'installation')
                )}
              </DataRow>
            ))}
          
          {/* Monthly Charges */}
          <tr>
            <SubHeading colSpan={visiblePlans.length} style={{ textAlign: 'left' }}>Monthly Charges</SubHeading>
            <SubHeading style={{ textAlign: 'center' }}>${getBusinessOptikTvPricing(formFields, {
              counters,
              checkboxes,
              dropdownSelections
            }).monthly.toFixed(2)}</SubHeading>
          </tr>
          
          {/* One-Time Charges */}
          <tr>
            <SubHeading colSpan={visiblePlans.length} style={{ textAlign: 'left' }}>One-Time Charges</SubHeading>
            <SubHeading style={{ textAlign: 'center' }}>${getBusinessOptikTvPricing(formFields, {
              counters,
              checkboxes,
              dropdownSelections
            }).oneTime.toFixed(2)}</SubHeading>
          </tr>
        </tbody>
              </Table>
        {error && <Error text={error.text} />}
      </TableWrapper>
  );
};

BusinessOptikTvTable.propTypes = {
  fieldName: PropTypes.string.isRequired,
  formFields: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    text: PropTypes.string
  }),
  initialData: PropTypes.shape({
    counters: PropTypes.shape({}),
    checkboxes: PropTypes.shape({}),
    dropdownSelections: PropTypes.shape({})
  })
};

export default BusinessOptikTvTable;
